/**
 * Personal Mode Limits for Clone Database
 *
 * Defines the database size limits for Personal mode when cloning with data.
 * Schema-only clones are not restricted.
 */

/**
 * Maximum database size in bytes for clone with data in Personal mode (1 GB).
 * Databases larger than this require Pro mode for data cloning.
 */
export const PERSONAL_MODE_MAX_SIZE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB

/**
 * Check result for Personal mode clone database limits
 */
export type PersonalModeLimitCheck = {
  /** Whether the database exceeds Personal mode limits */
  exceedsLimit: boolean;
  /** Database size in bytes (null if unknown) */
  sizeInBytes: number | null;
  /** Human-readable description of the limit status */
  description: string;
  /** Formatted size string for display */
  formattedSize: string | null;
};

/**
 * Checks if a database exceeds Personal mode size limits for cloning with data.
 *
 * In Personal mode, cloning with data is only allowed for databases
 * <= 1 GB. Larger databases require Pro mode for data cloning.
 * Schema-only clones are always allowed.
 *
 * @param sizeInBytes - Database size in bytes (null if unknown)
 * @returns Limit check result
 */
export function checkPersonalModeLimits(sizeInBytes: number | null): PersonalModeLimitCheck {
  // If size is null/unknown, assume it's within limits
  // (we can't block without knowing the size)
  const exceedsLimit = sizeInBytes !== null && sizeInBytes > PERSONAL_MODE_MAX_SIZE_BYTES;
  const formattedSize = sizeInBytes !== null ? formatBytes(sizeInBytes) : null;

  const description = getPersonalModeLimitDescription(sizeInBytes, exceedsLimit);

  return {
    exceedsLimit,
    sizeInBytes,
    description,
    formattedSize,
  };
}

/**
 * Generates a human-readable description of the limit status.
 */
function getPersonalModeLimitDescription(sizeInBytes: number | null, exceedsLimit: boolean): string {
  if (sizeInBytes === null) {
    return 'Unable to determine database size.';
  }

  const formattedSize = formatBytes(sizeInBytes);
  const limitString = formatBytes(PERSONAL_MODE_MAX_SIZE_BYTES);

  if (exceedsLimit) {
    return `Database size (${formattedSize}) exceeds the ${limitString} limit for cloning with data in Personal mode.`;
  }

  return `Database size (${formattedSize}) is within Personal mode limits.`;
}

/**
 * Formats the size limit for display in the UI.
 *
 * @returns Human-readable string describing the Personal mode limit
 */
export function getPersonalModeLimitString(): string {
  return formatBytes(PERSONAL_MODE_MAX_SIZE_BYTES);
}

/**
 * Formats bytes to a human-readable string.
 * Uses binary units (1 KB = 1024 bytes).
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  // Format with appropriate decimal places
  let decimals = 2;
  if (size >= 100) decimals = 0;
  else if (size >= 10) decimals = 1;

  return `${size.toFixed(decimals)} ${units[i]}`;
}

/**
 * Fetches the database size from the backend.
 * Reuses the backup-restore size estimation API.
 *
 * @param connectionId - Connection ID
 * @param databaseName - Database name
 * @param dbType - Database type ('mssql' | 'pgsql')
 * @returns Size in bytes or null if unable to fetch
 */
export async function fetchDatabaseSize(
  connectionId: string,
  databaseName: string,
  dbType: 'mssql' | 'pgsql'
): Promise<{ success: boolean; sizeInBytes?: number; error?: string }> {
  try {
    if (!window.electronAPI) {
      return { success: false, error: 'Electron API not available' };
    }
    const result = await window.electronAPI.backupRestore.getDatabaseSize(
      connectionId,
      databaseName,
      dbType
    );
    return result;
  } catch (error) {
    console.error('[clone-database/personalModeLimits] Error fetching database size:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching database size',
    };
  }
}
